import * as React from "react";
import { useQuery } from "convex/react";
import { useDroppable } from "@dnd-kit/core";
import { Home } from "lucide-react";
import { cn } from "@/lib/utils";
import { FolderTreeNode, type FolderNode } from "./FolderTreeNode";
import { api } from "$convex/convexApi";
import { Skeleton } from "@/components/ui/skeleton";

const STORAGE_KEY = "knowledgebase:expandedFolders";

function loadExpandedPaths(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return new Set();
    const parsed = JSON.parse(stored);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function saveExpandedPaths(paths: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(paths)));
  } catch {
    // Ignore storage errors
  }
}

type FolderTreeProps = {
  authUserId: string;
  selectedPath: string | null;
  onSelectFolder: (path: string) => void;
  onNewDocument?: (folderPath: string) => void;
  onNewFolder?: (parentPath: string) => void;
  onRenameFolder?: (folderPath: string, currentName: string) => void;
  onDeleteFolder?: (folderPath: string, folderName: string) => void;
};

export function FolderTree({
  authUserId,
  selectedPath,
  onSelectFolder,
  onNewDocument,
  onNewFolder,
  onRenameFolder,
  onDeleteFolder,
}: FolderTreeProps) {
  const [expandedPaths, setExpandedPaths] = React.useState<Set<string>>(
    loadExpandedPaths,
  );

  const { setNodeRef: setRootNodeRef, isOver: isRootOver } = useDroppable({
    id: "folder-root",
    data: {
      folderPath: "",
      folderName: "Root",
    },
  });

  const folderData = useQuery(
    api.knowledgeBase.getFolderTree,
    authUserId ? { authUserId } : "skip",
  );

  const handleToggle = React.useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      saveExpandedPaths(next);
      return next;
    });
  }, []);

  const handleSelect = React.useCallback(
    (path: string) => {
      onSelectFolder(path);
    },
    [onSelectFolder],
  );

  if (!folderData) {
    return (
      <div className="space-y-2 p-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (folderData.folders.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No folders yet. Create a document with a folder path to get started.
      </div>
    );
  }

  return (
    <div className="py-2">
      <div
        ref={setRootNodeRef}
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent",
          selectedPath === "" && "bg-accent font-medium",
          isRootOver && "bg-primary/10 ring-2 ring-primary",
        )}
        onClick={() => handleSelect("")}
      >
        <Home className="size-4 text-blue-600" />
        <span className="flex-1">Root</span>
      </div>
      {folderData.folders.map((node: FolderNode) => (
        <FolderTreeNode
          key={node.path}
          node={node}
          selectedPath={selectedPath}
          expandedPaths={expandedPaths}
          onToggle={handleToggle}
          onSelect={handleSelect}
          onNewDocument={onNewDocument}
          onNewFolder={onNewFolder}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
        />
      ))}
    </div>
  );
}
